import { useAppSelector } from "~/hooks";
import { GeneratingSpinner } from "../components/GeneratingSpinner";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { SectionWrapper } from "../components/SectionWrapper";
import { StaleWarning } from "../components/StaleWarning";
import type { SectionStatus } from "../hooks/usePageViewData";
import styles from "./FieldDesignSection.module.scss";

type FieldDesignSectionProps = {
  number: number;
  status: SectionStatus;
  detailPath: string;
  onGenerate: (documentType: string) => void;
  canRegenerate: boolean;
};

export const FieldDesignSection = ({
  number,
  status,
  detailPath,
  onGenerate,
  canRegenerate,
}: FieldDesignSectionProps) => {
  const { appData, isMarkdown } = useAppSelector((s) => s.fieldDesign);

  const renderContent = () => {
    if (isMarkdown) {
      return (
        <div className={styles["fallback"]}>
          一覧形式で表示できません。詳細ページで確認してください →
        </div>
      );
    }

    if (appData.length === 0) {
      return <div className={styles["fallback"]}>フィールド設計データがありません</div>;
    }

    const totalFields = appData.reduce((sum, app) => sum + app.properties.length, 0);
    const maxDisplay = 5;
    const displayApps = appData.slice(0, maxDisplay);
    const remainingCount = appData.length - maxDisplay;

    return (
      <>
        <ul className={styles["appList"]}>
          {displayApps.map((app) => {
            const maxFields = 10;
            const displayFields = app.properties.slice(0, maxFields);
            const remainingFields = app.properties.length - maxFields;
            return (
              <li key={app.app_id} className={styles["appCard"]}>
                <div className={styles["appHeader"]}>
                  <span className={styles["appName"]}>{app.app_name}</span>
                  <span className={styles["fieldBadge"]}>{app.properties.length} フィールド</span>
                </div>
                <ul className={styles["fieldList"]}>
                  {displayFields.map((field) => (
                    <li key={field.id ?? field.label} className={styles["fieldItem"]}>
                      {field.label}
                    </li>
                  ))}
                  {remainingFields > 0 && (
                    <li className={styles["fieldMore"]}>他 {remainingFields} 件</li>
                  )}
                </ul>
              </li>
            );
          })}
        </ul>
        {remainingCount > 0 && (
          <p className={styles["remaining"]}>他 {remainingCount} 件のアプリ</p>
        )}
        <p className={styles["summary"]}>
          合計: アプリ {appData.length}件 / フィールド {totalFields}件
        </p>
      </>
    );
  };

  if (status === "generating") {
    return (
      <SectionWrapper
        number={number}
        title="作成するフィールド（フィールド設計書）"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("field-design")}
        disableGenerate={!canRegenerate}
      >
        <GeneratingSpinner />
      </SectionWrapper>
    );
  }

  if (status === "stale") {
    return (
      <SectionWrapper
        number={number}
        title="作成するフィールド（フィールド設計書）"
        status={status}
        detailPath={detailPath}
        onGenerate={() => onGenerate("field-design")}
        disableGenerate={!canRegenerate}
      >
        <SectionErrorBoundary
          sectionName="作成するフィールド（フィールド設計書）"
          detailPath={detailPath}
        >
          {renderContent()}
        </SectionErrorBoundary>
        <StaleWarning />
      </SectionWrapper>
    );
  }

  return (
    <SectionWrapper
      number={number}
      title="作成するフィールド（フィールド設計書）"
      status={status}
      detailPath={detailPath}
      onGenerate={() => onGenerate("field-design")}
      disableGenerate={!canRegenerate}
    >
      <SectionErrorBoundary
        sectionName="作成するフィールド（フィールド設計書）"
        detailPath={detailPath}
      >
        {renderContent()}
      </SectionErrorBoundary>
    </SectionWrapper>
  );
};
